import React from "react";
import { Counterparty } from "@common/types/counterparty";
import {
    RuleActionType,
    TransactionRuleAction,
    TransferRuleDirection,
    parseAccountIdActionValue,
    parseAccrualAccountActionValue,
} from "@common/types/transactionRule";
import { Tag } from "@design-system/Tag/Tag";
import { RULE_TAG_PROPS } from "../ruleTagUtils";
import { RuleCategoryAction } from "./RuleCategoryAction";
import { RuleEntityAction } from "./RuleEntityAction";
import { RuleAccountAction } from "./RuleAccountAction";
import { RuleMemoAction } from "./RuleMemoAction";
import { RuleCounterpartyAction } from "./RuleCounterpartyAction";
import { RuleCustomerAction } from "./RuleCustomerAction";
import { RuleAccountNameAction } from "./RuleAccountNameAction";
import { RuleClassesAction } from "./RuleClassesAction";
import { RuleSplitAction } from "./RuleSplitAction";
import { RuleScheduleAction } from "./RuleScheduleAction";

interface Props {
    action: TransactionRuleAction;
    counterparties: Counterparty[];
    transferDirection: TransferRuleDirection | null;
}

export const RuleAction: React.FC<Props> = ({
    action,
    counterparties,
    transferDirection,
}) => {
    switch (action.actionType) {
        case RuleActionType.CATEGORY:
            return (
                <RuleCategoryAction
                    value={action.value as string}
                    transferDirection={transferDirection}
                />
            );
        case RuleActionType.ENTITY:
            return <RuleEntityAction value={action.value as number} />;
        case RuleActionType.ACCOUNT_ID:
            return (
                <RuleAccountAction
                    values={parseAccountIdActionValue(action.value) ?? []}
                />
            );
        case RuleActionType.ACCOUNT_NAME:
            return <RuleAccountNameAction value={action.value as string} />;
        case RuleActionType.ACCRUAL_ACCOUNT_ID: {
            const parsed = parseAccrualAccountActionValue(action.value);
            const label = parsed.autoConfirm
                ? "Auto confirm accrual ledger"
                : "Assign to accrual ledger";
            return (
                <RuleAccountAction
                    values={parsed.accountIds ?? []}
                    label={label}
                    isAccrual
                />
            );
        }
        case RuleActionType.ACCRUAL_ACCOUNT_NAME: {
            const parsed = parseAccrualAccountActionValue(action.value);
            const label = parsed.autoConfirm
                ? "Auto confirm accrual ledger"
                : "Assign to accrual ledger";
            return (
                <RuleAccountNameAction
                    value={parsed.accountName ?? ""}
                    label={label}
                />
            );
        }
        case RuleActionType.MEMO:
            return <RuleMemoAction value={action.value as string} />;
        case RuleActionType.SPLIT:
            return <RuleSplitAction value={action.value as string} />;
        case RuleActionType.COUNTERPARTY:
            return (
                <RuleCounterpartyAction
                    value={action.value as string}
                    counterparties={counterparties}
                />
            );
        case RuleActionType.CUSTOMER:
            return (
                <RuleCustomerAction
                    value={action.value as string}
                    counterparties={counterparties}
                />
            );
        case RuleActionType.CLASS:
            return <RuleClassesAction value={action.value as string[]} />;
        case RuleActionType.ASSIGN_TO_SCHEDULE:
            if (typeof action.value !== "string") {
                return null;
            }
            return <RuleScheduleAction value={action.value} />;
        case RuleActionType.TRANSFER_MATCHING:
            return <RuleTransferMatchingAction />;
        default:
            return null;
    }
};

const RuleTransferMatchingAction: React.FC = () => (
    <Tag {...RULE_TAG_PROPS}>Disallow matching</Tag>
);
